/**
 * RAG Compliance Finding Service
 *
 * Wraps the Railway routes that ran as the `rag-compliance-check` Supabase
 * edge function. The prior service's `getConfiguredProvider` is gone —
 * provider resolution lives server-side now (body override > settings >
 * 'anthropic' default).
 */

import type { DeleteResult, ListResult, ServiceContext } from "@prolarus/shared";
import { ApiError, apiDelete, apiGet, apiPost } from "@/lib/apiClient";

export interface RagComplianceFinding {
  id: string;
  company_id: string;
  invoice_id: string;
  invoice_item_id: string | null;
  finding_type: string;
  severity: string;
  title: string;
  description: string;
  source_chunk_ids: string[];
  source_citations: string[];
  estimated_impact: number | null;
  ai_provider: string | null;
  ai_model: string | null;
  created: string;
  created_by: string | null;
}

export type AIProvider = "anthropic" | "openai";

interface RunResponse {
  success: boolean;
  findings?: RagComplianceFinding[];
  chunks_retrieved?: number;
  ai_provider?: AIProvider;
  ai_model?: string;
  processing_time_ms?: number;
  error?: string;
}

function toError(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

export const ragComplianceFindingService = {
  async listByInvoice(invoiceId: string): Promise<ListResult<RagComplianceFinding>> {
    try {
      const result = await apiGet<{ data: RagComplianceFinding[] }>(
        `/api/compliance/rag/by-invoice/${encodeURIComponent(invoiceId)}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [], error: toError(err) };
    }
  },

  /**
   * Run the RAG compliance check for an invoice. Provider is resolved
   * server-side from the company settings; the optional aiProvider arg
   * forces an explicit provider for this run (rarely used).
   */
  async runForInvoice(
    invoiceId: string,
    _context: ServiceContext,
    aiProvider?: AIProvider,
  ): Promise<{ data: RagComplianceFinding[] | null; error: Error | null }> {
    try {
      const data = await apiPost<RunResponse>("/api/compliance/rag/run", {
        invoice_id: invoiceId,
        ...(aiProvider ? { ai_provider: aiProvider } : {}),
      });
      if (!data.success) {
        return { data: null, error: new Error(data.error ?? "RAG compliance check failed") };
      }
      return { data: data.findings ?? [], error: null };
    } catch (err) {
      return { data: null, error: toError(err) };
    }
  },

  async deleteByInvoice(
    invoiceId: string,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    try {
      const result = await apiDelete<{ success: boolean; changesLogged?: boolean }>(
        `/api/compliance/rag/by-invoice/${encodeURIComponent(invoiceId)}`,
      );
      return { success: result.success, error: null, changesLogged: !!result.changesLogged };
    } catch (err) {
      return { success: false, error: toError(err), changesLogged: false };
    }
  },
};
